import QRCode from 'qrcode'
import { Show, createEffect, createSignal, onCleanup } from 'solid-js'
import { holdScreenAwake, wakeLockSupported, wakeLockUnavailableReason } from '../../lib/wakelock'

/**
 * The guest's standing ZipPass QR.
 *
 * This is the pass a bouncer scans to recognise the guest — it is issued
 * server-side (`passes.qr_payload`) and is NOT the per-check-in QR the counter
 * code produces. It arrives through `loadHub()` and, because the hub caches its
 * state, it renders with no signal — which is the point: a cloakroom is a
 * basement.
 *
 * Scanner physics, not styling: a QR on a dark background fails many readers, so
 * the code is always black-on-white inset on a white tile, at a generous size.
 * There is no web API to raise screen brightness, so instead of faking one this
 * offers a full-screen mode that holds the display awake while it is open.
 */
export default function PassQr(props: { qrPayload: string | null; style?: string }) {
  const [png, setPng] = createSignal('')
  const [expanded, setExpanded] = createSignal(false)

  createEffect(() => {
    const payload = props.qrPayload
    if (!payload) {
      setPng('')
      return
    }
    QRCode.toDataURL(payload, { width: 512, margin: 1, errorCorrectionLevel: 'M' })
      .then(setPng)
      .catch(() => setPng(''))
  })

  return (
    <>
      {/* Inline tile — white inset so it stays scannable on the dark hub. */}
      <div class={props.style ?? ''}>
        <Show
          when={props.qrPayload}
          fallback={
            <div class="w-full aspect-square rounded-xl border border-white/10 bg-white/5 flex items-center justify-center p-6">
              <p class="text-xs text-white/50 text-center">Your pass is being prepared. Pull down to refresh.</p>
            </div>
          }
        >
          <button
            type="button"
            onClick={() => setExpanded(true)}
            class="block w-full rounded-2xl bg-white p-3 hover:brightness-95 transition"
            aria-label="Show your ZipPass full screen"
          >
            <Show when={png()} fallback={<div class="w-full aspect-square animate-pulse bg-gray-200 rounded" />}>
              <img src={png()} alt="Your ZipPass QR code" class="w-full aspect-square" />
            </Show>
          </button>
          <p class="mt-2 text-[10px] text-white/40 text-center">Tap for full screen at the counter</p>
        </Show>
      </div>

      {/* Full-screen mode: max size, held awake, plain white for contrast. */}
      <Show when={expanded()}>
        <FullScreenQr png={png()} payload={props.qrPayload ?? ''} onClose={() => setExpanded(false)} />
      </Show>
    </>
  )
}

function FullScreenQr(props: { png: string; payload: string; onClose: () => void }) {
  // Hold the display on while the pass is being shown; release when they close.
  createEffect(() => {
    let release: (() => void) | null = null
    holdScreenAwake().then((fn) => {
      release = fn
    })
    onCleanup(() => release?.())
  })

  return (
    <div class="fixed inset-0 z-50 bg-white flex flex-col items-center justify-center p-6">
      <img src={props.png} alt="Your ZipPass QR code" class="w-full max-w-sm aspect-square" />
      <p class="mt-4 text-xs text-gray-500 text-center max-w-sm break-all">{props.payload}</p>
      <p class="mt-3 text-[11px] text-gray-400 text-center max-w-sm">
        {wakeLockSupported()
          ? 'Screen kept awake while this is open.'
          : `Screen brightness is controlled by your phone (${wakeLockUnavailableReason()}).`}
      </p>
      <button
        type="button"
        onClick={() => props.onClose()}
        class="mt-6 px-6 py-3 rounded-xl bg-[#1a1a2e] text-white font-semibold"
      >
        Done
      </button>
    </div>
  )
}
